<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import { type FortuneWheelPrizeV2 } from '@/types/shared/fortune-wheel'
import ChestJson from '@/assets/lotties/chest.json'
import { Vue3Lottie } from 'vue3-lottie'
import { T, useTranslate } from '@tolgee/vue'
// Background images for prize cards

import GreenCardBg from '@/assets/images/prize-cards/green-card-background.webp'
import RedCardBg from '@/assets/images/prize-cards/red-card-background.webp'
import SpinsImage from '@/assets/images/fortune-wheel-cards/spin-bonus.webp'

// Special images (kept for tickets and feathers)
import TicketImage from '@/assets/images/gifs/ticket.gif'
import FeatherImage from '@/assets/images/feather-large.png'
import IdeaImage from '@/assets/images/special-currencies/idea.webp'

import StrokeCoinImage from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import { parseProfitHours } from '@/helpers/profit-time'
import { useConfig } from '@/composables/game/config'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import BaseButton from '@/components/base/BaseButton.vue'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { twMerge } from 'tailwind-merge'
import { useScreenSize } from '@/composables/helpers/screen-size'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'

const props = withDefaults(defineProps<{
	show: boolean
	prize: FortuneWheelPrizeV2
	appliedMultiplier?: number
}>(), {
  appliedMultiplier: 1,
})

const emit = defineEmits<{
	(e: 'ctaClick'): void
	(e: 'onCheckEntries'): void
}>()

// TypeScript interface for multi-sized text parts
interface TextPart {
	text: string
	size: 'large' | 'small'
}

const { topStyle } = useFullScreenDimensions()
const { t } = useTranslate()
const { config } = useConfig()
const isClickable = ref(false)
const showChest = ref(false)
const showImage = ref(false)
const showText = ref(false)

function extractPrizeType(prizeId: string): string | null {
  const prizeBoxMatch = prizeId.match(/^prize_box_(.+?)_\d+/)
  if (prizeBoxMatch) {
    return prizeBoxMatch[1]
  }

  const directMatch = prizeId.match(/^([a-zA-Z]+)_/)
  if (directMatch) {
    return directMatch[1]
  }

  return null
}

function isPrizeType(prizeId: string, type: string): boolean {
  const extracted = extractPrizeType(prizeId)
  if (!extracted) return false

  const normalizedExtracted = extracted.toLowerCase().replace(/_/g, '')
  const normalizedType = type.toLowerCase().replace(/_/g, '')

  return normalizedExtracted.includes(normalizedType)
}

const prizeTypeBackgrounds: Record<string, string> = {
  double_profit: GreenCardBg,
  doubleprofit: GreenCardBg,
  offline_profit: RedCardBg,
  offlineprofit: RedCardBg,
  spin: SpinsImage,
  golden_coin: GreenCardBg,
  goldencoin: GreenCardBg,
  ticket: TicketImage,
  golden_feather: FeatherImage,
  goldenfeather: FeatherImage,
  idea: IdeaImage,
}

function getPrizeBackground(prizeId: string): string | undefined {
  const extracted = extractPrizeType(prizeId)
  if (!extracted) return undefined

  const normalized = extracted.toLowerCase().replace(/_/g, '')

  return prizeTypeBackgrounds[extracted] ?? prizeTypeBackgrounds[normalized]
}

function getPrizeText(prizeId: string, amount: number): TextPart[] | null {
  if (isPrizeType(prizeId, 'double_profit')) {
    return [
      { text: 'PPH', size: 'large' },
      { text: 'x2', size: 'large' },
    ]
  }

  if (isPrizeType(prizeId, 'offline_profit')) {
    const { hours, minutes, displayInHours } = parseProfitHours(amount)
    const displayValue = displayInHours ? hours : minutes
    const unit = displayInHours ? 'H' : 'MIN'
    return [
      { text: `+${displayValue}`, size: 'large' },
      { text: unit, size: 'large' },
      { text: 'OFFLINE PROFIT', size: 'small' },
    ]
  }

  if (isPrizeType(prizeId, 'spin')) {
    return [
      { text: `${amount}`, size: 'large' },
      { text: 'spins', size: 'small' },
    ]
  }

  return null
}

const prizeLabel = computed(() => {
  const isDoubleProfit = props.prize.id.includes('double_profit') ||
		props.prize.id.includes('doubleProfit') ||
		props.prize.currency === 'doubleProfit'
  const isOfflineProfit = props.prize.id.includes('offline_profit') ||
		props.prize.id.includes('offlineProfit') ||
		props.prize.currency === 'offlineProfit'

  if (!isDoubleProfit && !isOfflineProfit) return ''

  const { hours, minutes, displayInHours } = parseProfitHours(props.prize.amount)

  if (isDoubleProfit) {
    if (displayInHours) {
      return t.value('fortune_wheel.chest_prize.double_profit_hours_label',
        'Profit per hour doubled for {hours} {hours, plural, one {hour} other {hours}}!',
        { hours })
    }
    return t.value('fortune_wheel.chest_prize.double_profit_minutes_label',
      'Profit per hour doubled for {minutes} minutes!',
      { minutes })
  }

  if (isOfflineProfit) {
    if (displayInHours) {
      return t.value('fortune_wheel.chest_prize.offline_profit_hours_label',
        'Extra {hours} {hours, plural, one {hour} other {hours}} on top of your 3-hour profits!',
        { hours })
    }
    return t.value('fortune_wheel.chest_prize.offline_profit_minutes_label',
      'Extra {minutes} minutes on top of your 3-hour profits!',
      { minutes })
  }

  return ''
})

const currencyFallbackBackground: Record<string, string> = {
  spin: SpinsImage,
  goldenCoin: GreenCardBg,
  giveawayTicket: TicketImage,
  goldFeather: FeatherImage,
  idea: IdeaImage,
  doubleProfit: GreenCardBg,
  offlineProfit: RedCardBg,
}

const currentBackground = computed(() => {
  const bgFromId = getPrizeBackground(props.prize.id)
  if (bgFromId) return bgFromId

  const currencyFallback = currencyFallbackBackground[props.prize.currency]
  if (currencyFallback) return currencyFallback

  return liveOpsSpecialReward.value?.image ?? undefined
})

const currentText = computed<TextPart[] | null | undefined>(() => {
  return getPrizeText(props.prize.id, props.prize.amount)
})

const coinReward = computed<null | number>(() => {
  if (props.prize.currency !== 'goldenCoin') return null
  return props.prize.amount * config.value.game.ecommerce.currencies.goldenCoin
})

const spinReward = computed<null | number>(() => {
  if (props.prize.currency !== 'spin') return null
  return props.prize.amount
})

const ticketReward = computed<null | number>(() => {
  if (props.prize.currency !== 'giveawayTicket') return null
  return props.prize.amount
})

const featherReward = computed<null | number>(() => {
  if (props.prize.currency !== 'goldFeather') return null
  return props.prize.amount
})

const ideaReward = computed<null | number>(() => {
  if (props.prize.currency !== 'idea') return null
  return props.prize.amount
})

// Live ops currencies not explicitly handled above
const explicitlyHandledCurrencies = ['goldenCoin', 'spin', 'giveawayTicket', 'goldFeather', 'idea']
const liveOpsSpecialReward = computed(() => {
  if (explicitlyHandledCurrencies.includes(props.prize.currency)) return null
  return allSpecialCurrenciesConfig.value[props.prize.currency] ?? null
})

function onAnimationLoaded() {
  setTimeout(() => showImage.value = true, 2000)
}

const { addHapticFeedback } = useHapticFeedback()

function onContainerClick() {
  if (isClickable.value) {
    addHapticFeedback('light')
    emit('ctaClick')
  }
}

watch(() => props.show, show => {
  if (show) {
    document.body.classList.add('!overflow-hidden')
    setTimeout(() => showChest.value = true, 300)
  } else {
    document.body.classList.remove('!overflow-hidden')
    isClickable.value = false
    showChest.value = false
    showImage.value = false
  }
}, { immediate: true })

watch(showImage, showImage => {
  if (!showImage) {
    showText.value = false
    return
  }
  setTimeout(() => {
    showText.value = true
    isClickable.value = true
  }, 1400)
})

useWatchOverlays(computed(() => props.show))
const { width } = useScreenSize()

</script>
<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 bg-dark-most-bottom-layer/[.65] backdrop-blur-[4px] flex flex-col z-[800]"
      @click="onContainerClick"
    >
      <CrossCircleIcon
        v-if="isClickable"
        class="w-6 h-6 min-w-[24px] absolute top-3 right-3 text-white/[87%]"
        :style="topStyle"
      />
      <div
        class="h-full w-full flex-1 flex flex-col gap-y-8 items-center justify-center relative mb-[60px] min-[380px]:mb-[80px]"
      >
        <div
          class="w-[90%] h-0 pb-[90%] absolute rounded-full bg-teal-primary/[80] blur-[100px]"
        />
        <img
          class="absolute w-full h-auto"
          src="@/assets/images/goldwheel-overlay-bg.webp"
        >
        <div class="z-[2] w-full flex items-center justify-center relative">
          <Transition name="fade">
            <Vue3Lottie
              v-if="showChest"
              :animation-data="ChestJson"
              :auto-play="true"
              :loop="false"
              width="100%"
              height="auto"
              class="min-w-[512px] max-w-[512px] min-w-[380px]:min-w-[532px]"
              :class="{'animate-blur': showImage}"
              @on-animation-loaded="onAnimationLoaded"
            />
          </Transition>
          <div class="absolute flex flex-col items-center top-1/2 -translate-y-1/2">
            <div
              v-if="showImage"
              class="relative w-full max-w-[368px]"
            >
              <Transition name="fade">
                <div
                  v-if="showText"
                  class="absolute -top-[58px] left-1/2 -translate-x-1/2 text-shadow text-[36px] leading-[42px] font-bold min-w-max"
                >
                  <T
                    key-name="fortune_wheel.chest_prize.title"
                    default-value="Bonus unlocked!"
                  />
                </div>
              </Transition>
              <div
                :class="twMerge('my-8 max-w-full scale-in-image relative overflow-hidden rounded-3xl', !ticketReward && 'card-shadow')"
                :style="'height: 254px; width: 200px'"
              >
                <!-- Background image -->
                <img
                  class="absolute object-cover size-full object-center inset-0"
                  :class="featherReward && 'rotate-[23deg] !object-contain'"
                  :src="currentBackground"
                >
                <div
                  v-if="!featherReward && !ticketReward && !ideaReward && !liveOpsSpecialReward"
                  class="absolute inset-0 size-full rounded-3xl border-2 border-white/20"
                />

                <!-- Text overlay for prizes (except coins which have their own overlay) -->
                <div
                  v-if="currentText && !coinReward"
                  class="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 flex flex-col items-center justify-center w-full px-4 text-center gap-y-1"
                  :class="{'mt-11': spinReward }"
                >
                  <span
                    v-for="(part, index) in currentText"
                    :key="index"
                    :class="[
                      'black-title-golden-stroke !font-bold',
                      spinReward ? (part.size === 'large' ? '!text-[65px]' : '!text-[60px] !leading-none') : (part.size === 'small' && '!text-[37px] !leading-none')
                    ]"
                  >
                    {{ part.text }}
                  </span>
                </div>

                <!-- Coin overlay (unchanged) -->
                <div
                  v-if="coinReward"
                  class="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 flex gap-1.5 items-center w-full justify-center"
                >
                  <img
                    :src="StrokeCoinImage"
                    class="h-7 w-auto object-contain"
                  >
                  <span
                    class="text-[32px] leading-[25px] font-[900] text-white black-stroke disable-slashed-zero"
                  >
                    {{
                      formatNumberFullTruncate(coinReward)
                    }}
                  </span>
                </div>
              </div>

              <Transition name="fade">
                <div
                  v-if="showText"
                  class="absolute  left-1/2 -translate-x-1/2 flex flex-col items-center w-screen max-w-[368px]"
                  :class="ticketReward ? '-bottom-[160px]' : '-bottom-[128px]'"
                >
                  <div class="text-white/60 text-2xl font-medium">
                    <T
                      key-name="fortune_wheel.chest_prize.you_won"
                      default-value="You won"
                    />
                  </div>
                  <div
                    class="flex items-center text-light-white text-[28px] leading-8 font-bold -tracking-[0.56px] text-center px-4"
                  >
                    <div
                      v-if="coinReward"
                      class="flex gap-1.5 items-center"
                      v-tooltip="{content: formatUnshortenedNumber(coinReward), placement: 'bottom'}"
                    >
                      <img
                        :src="CoinIcon"
                        class="h-7 w-auto text-white"
                      >
                      <span
                        class="text-[28px] leading-[25px] font-[700] text-white disable-slashed-zero"
                      >
                        {{
                          formatNumberFullTruncate(coinReward)
                        }}
                      </span>
                    </div>
                    <div v-else-if="spinReward">
                      <T
                        key-name="fortune_wheel.info.more_spins.count_spin"
                        default-value="{count, plural, one {# spin} other {# spins}}"
                        :params="{count: spinReward}"
                      />
                    </div>
                    <div v-else-if="featherReward">
                      <T
                        key-name="fortune_wheel.chest_prize.feathers_reward"
                        default-value="{count, plural, one {# Gold Feather} other {# Gold Feathers}}"
                        :params="{count: featherReward}"
                      />
                    </div>
                    <div v-else-if="ideaReward">
                      <T
                        key-name="fortune_wheel.chest_prize.ideas_reward"
                        default-value="{count, plural, one {# Idea} other {# Ideas}}"
                        :params="{count: ideaReward}"
                      />
                    </div>
                    <div v-else-if="ticketReward">
                      <T
                        key-name="fortune_wheel.chest_prize.ticket_reward"
                        default-value="{count, plural, one {# Lucky Rush ticket} other {# Lucky Rush tickets}}"
                        :params="{count: ticketReward}"
                      />
                    </div>
                    <div v-else-if="liveOpsSpecialReward">
                      {{ prize.amount }} {{ liveOpsSpecialReward.label }}
                    </div>
                    <div
                      v-else
                    >
                      {{ prizeLabel }}
                    </div>
                  </div>
                  <div
                    v-if="appliedMultiplier > 1"
                    class="text-white/60 text-xs font-medium mt-2"
                  >
                    x{{ appliedMultiplier }} Multiplier applied
                  </div>
                  <div
                    v-if="ticketReward"
                    @click.stop.prevent="$emit('onCheckEntries')"
                    class="mt-8"
                  >
                    <BaseButton
                      class="min-w-[286px]"
                    >
                      <T
                        key-name="fortune_wheel.ticket_overlay.cta"
                        default-value="CHECK MY ENTRIES"
                      />
                    </BaseButton>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.fade-enter-active, .fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to /* .fade-leave-active in <2.1.8 */
{
	opacity: 0;
}

.scale-in-image {
	animation: scale-in-image 1.4s ease-out;
}

.card-shadow {
	filter: drop-shadow(0 12px 24px rgba(0, 0, 0, 0.7));
}

.animate-blur {
	animation: blur-image 1s ease-out forwards;
	animation-delay: 0.4s;
}

@keyframes scale-in-image {
	0% {
		opacity: 0;
		transform: scale(0);
	}
	20% {
		opacity: 1; /* Ensure it starts becoming visible */
	}
	70% {
		transform: scale(1.1);
	}
	100% {
		transform: scale(1);
	}
}

@keyframes blur-image {
	0% {
		filter: blur(0);
	}
	100% {
		filter: blur(5px);
	}
}

.text-shadow {
	text-shadow: 0px 0px 24px rgba(0, 0, 0, 0.44)
}

.black-stroke {
	text-shadow: -1.5px -1.5px 0 #000,
	1.5px -1.5px 0 #000,
	-1.5px 1.5px 0 #000,
	1.5px 1.5px 0 #000,
	-2px 0 0 #000,
	2px 0 0 #000,
	0 -2px 0 #000,
	0 2px 0 #000,
	-1px -1px 0 #000, /* Finer shadow adjustments */
	1px -1px 0 #000,
	-1px 1px 0 #000,
	1px 1px 0 #000;
}
</style>